import { first, isEmpty } from 'lodash-es';

import { MENU_ITEM__FORMS__ASSIGNED } from '../../../constants';

export const getDefaultEmptyValue = (field) => {
  switch (field.type) {
    case 'array': {
      return [];
    }
    case 'boolean': {
      return false;
    }
    case 'integer': {
      return undefined;
    }
    case 'number': {
      return undefined;
    }
    case 'string': {
      if (field.dictionaryId) {
        return undefined;
      }
      return undefined;
    }
    default:
      return undefined;
  }
};

const getFullBase64Content = (rawBase64String, fileName) => {
  if (rawBase64String) {
    let fullBase64String = rawBase64String;

    if (!rawBase64String?.startsWith('data:')) {
      fullBase64String = `data:application/octet-stream;name=${fileName};base64,${rawBase64String}`;
    } else {
      fullBase64String = fullBase64String.replace(';base64,', `;name=${fileName};base64,`);
    }

    return fullBase64String;
  }

  return undefined;
};

const getFileValue = (values, fieldId) => {
  const file = values.find((value) => value.name === fieldId);

  if (file) {
    // Przeformatowany base zawierający nazwę lub sama nazwa pliku (zachowanie kompatybilności wstecznej).
    return getFullBase64Content(file.value, file.fileName) || file.fileName || undefined;
  }

  return undefined;
};

const getValue = (fields, values, fieldId) => {
  const fieldValue = values?.find((value) => value.name === fieldId);

  if (fields[fieldId].type === 'array') {
    return !isEmpty(fieldValue?.values) ? fieldValue.values : getDefaultEmptyValue(fields[fieldId]);
  }

  return first(values.filter(((value) => value.name === fieldId)))?.value || getDefaultEmptyValue(fields[fieldId]);
};

const getDefaultValue = (field) => {
  const defaultValue = field?.default ?? field?.defaultValue ?? undefined;

  if (field?.type === 'array') {
    return defaultValue != null ? [defaultValue] : getDefaultEmptyValue(field);
  }
  return defaultValue ?? getDefaultEmptyValue(field);
};

const getFieldValueByType = (fieldSchema, fieldData, isDefaultValueActive = false) => {
  const { fileName, value, values } = fieldData || {};
  const currentFieldValue = isDefaultValueActive
    ? value ?? getDefaultValue(fieldSchema)
    : value ?? undefined;

  if (fieldSchema?.format === 'data-url') {
    return getFullBase64Content(currentFieldValue, fileName);
  }

  if (fieldSchema?.type === 'integer') {
    return currentFieldValue != null ? parseInt(currentFieldValue, 10) : currentFieldValue;
  }

  if (fieldSchema?.type === 'number') {
    return currentFieldValue != null ? parseFloat(currentFieldValue) : currentFieldValue;
  }

  if (fieldSchema?.type === 'boolean') {
    return [true, 'true', 'True'].includes(currentFieldValue);
  }

  if (fieldSchema?.type === 'array') {
    return values?.map?.((item) => getFieldValueByType(fieldSchema.items, { ...fieldData, value: item, values: null }), isDefaultValueActive) || [];
  }

  return currentFieldValue ?? values ?? undefined;
};

export const convertToReactJsonSchemaFormData = (selectedMenuItem, fields, parentId, initialEditorState, values, required) => {
  if (Object.keys(fields).length > 0) {
    Object.keys(fields).forEach((fieldId) => {
      if (!fields[fieldId].nested && fields[fieldId].type === 'object') {
        // Obsługa pól zdefiniowanych w sekcjach.

        if (!initialEditorState[fieldId]) {
          initialEditorState[fieldId] = {};
        }

        convertToReactJsonSchemaFormData(selectedMenuItem, fields[fieldId].properties, fieldId, initialEditorState[fieldId], values);
      } else if (fields[fieldId].nested) {
        // Obsługa pól zagnieżdżonych.

        if (fields[fieldId].type === 'array') {
          // Obsługa WIELOKROTNYCH pól zagnieżdżonych.

          const fieldValue = [];

          const nestedCollection = first(values.filter(((value) => value.name === fieldId)))?.nestedCollection;
          if (nestedCollection && nestedCollection.length > 0) {
            nestedCollection.forEach((nestedItem) => {
              const nestedValue = {};
              // eslint-disable-next-line no-underscore-dangle
              nestedValue._id = nestedItem.id;

              nestedItem.fields.forEach((value) => {
                const parentFieldSchema = fields?.[fieldId];
                const fieldSchema = parentFieldSchema?.items?.properties?.[value.name];
                const isDefaultValueActive = selectedMenuItem === MENU_ITEM__FORMS__ASSIGNED;

                nestedValue[value.name] = getFieldValueByType(fieldSchema, value, isDefaultValueActive);
              });

              fieldValue.push(nestedValue);
            });
          }

          if (required.includes(fieldId) && fieldValue?.length === 0) {
            fieldValue.push({});
          }

          if (parentId) {
            if (!initialEditorState[parentId]) {
              initialEditorState[parentId] = null;
            }

            initialEditorState[parentId] = {
              ...initialEditorState[parentId],
              [fieldId]: fieldValue,
            };
          } else {
            initialEditorState[fieldId] = fieldValue;
          }
        }

        if (fields[fieldId].type !== 'array') {
          // Obsługa JEDNOKROTNYCH pól zagnieżdżonych.
          initialEditorState[fieldId] = {};

          const fieldValues = first(values.filter(((value) => value.name === fieldId)));
          const nestedValues = fieldValues?.nested?.fields;
          convertToReactJsonSchemaFormData(selectedMenuItem, fields[fieldId].properties, fieldId, initialEditorState[fieldId], nestedValues);
        }
      } else {
        // Obsługa pól standardowych.

        const isDefaultValueActive = selectedMenuItem === MENU_ITEM__FORMS__ASSIGNED;
        const fieldData = values?.find?.((field) => field.name === fieldId);

        initialEditorState[fieldId] = getFieldValueByType(fields[fieldId], fieldData, isDefaultValueActive);
      }
    });
  }
};
